import Quill from 'quill';

const MIN_IMAGE_WIDTH = 40;
const HANDLES = ['nw', 'ne', 'sw', 'se'] as const;
type ResizeHandle = typeof HANDLES[number];

export class QuillImageResize {
  private readonly quill: any;
  private img: HTMLImageElement | null = null;
  private overlay: HTMLDivElement | null = null;
  private dragHandle: ResizeHandle | null = null;
  private startX = 0;
  private startWidth = 0;

  constructor(quill: any) {
    this.quill = quill;
    this.quill.root.addEventListener('click', (event: MouseEvent) => this.onEditorClick(event));
    this.quill.root.addEventListener('scroll', () => this.repositionOverlay());
    this.quill.root.addEventListener('dragstart', (event: DragEvent) => {
      if ((event.target as HTMLElement)?.tagName === 'IMG') {
        event.preventDefault();
      }
    });
    this.quill.on('text-change', () => {
      if (this.img && !this.quill.root.contains(this.img)) {
        this.hide();
        return;
      }
      this.repositionOverlay();
    });
    document.addEventListener('click', (event: MouseEvent) => this.onDocumentClick(event));
  }

  private onEditorClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.tagName === 'IMG') {
      event.stopPropagation();
      this.show(target as HTMLImageElement);
      return;
    }
    this.hide();
  }

  private onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (!this.overlay || !target) {
      return;
    }
    if (this.overlay.contains(target) || target === this.img) {
      return;
    }
    this.hide();
  }

  private show(img: HTMLImageElement): void {
    if (this.img === img && this.overlay) {
      this.repositionOverlay();
      return;
    }
    this.hide();
    this.img = img;
    this.overlay = document.createElement('div');
    this.overlay.className = 'ql-image-resize-overlay';
    HANDLES.forEach(handle => {
      const handleEl = document.createElement('div');
      handleEl.className = `ql-image-resize-handle ql-image-resize-handle--${handle}`;
      handleEl.addEventListener('mousedown', event => this.onDragStart(event, handle));
      this.overlay.appendChild(handleEl);
    });
    this.getOverlayParent().appendChild(this.overlay);
    this.repositionOverlay();
  }

  private hide(): void {
    this.overlay?.remove();
    this.overlay = null;
    this.img = null;
    this.dragHandle = null;
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.onDragEnd);
  }

  private onDragStart(event: MouseEvent, handle: ResizeHandle): void {
    if (!this.img) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.dragHandle = handle;
    this.startX = event.clientX;
    this.startWidth = this.img.getBoundingClientRect().width;
    document.addEventListener('mousemove', this.onDrag);
    document.addEventListener('mouseup', this.onDragEnd);
  }

  private readonly onDrag = (event: MouseEvent): void => {
    if (!this.img || !this.dragHandle) {
      return;
    }
    const deltaX = event.clientX - this.startX;
    const growsToTheRight = this.dragHandle === 'ne' || this.dragHandle === 'se';
    const nextWidth = growsToTheRight ? this.startWidth + deltaX : this.startWidth - deltaX;
    this.applyWidth(nextWidth);
    this.repositionOverlay();
  };

  private readonly onDragEnd = (): void => {
    this.dragHandle = null;
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.onDragEnd);
    this.quill.update('user');
  };

  private applyWidth(width: number): void {
    if (!this.img) {
      return;
    }
    const maxWidth = Math.max(MIN_IMAGE_WIDTH, this.quill.root.clientWidth - 8);
    const rounded = Math.round(Math.min(maxWidth, Math.max(MIN_IMAGE_WIDTH, width)));
    this.img.setAttribute('width', String(rounded));
    this.img.style.width = `${rounded}px`;
    this.img.style.height = 'auto';
    this.img.removeAttribute('height');

    const blot = Quill.find(this.img) as { format?: (name: string, value: string) => void } | null;
    blot?.format?.('width', String(rounded));
  }

  private repositionOverlay(): void {
    if (!this.img || !this.overlay) {
      return;
    }
    const parent = this.getOverlayParent();
    const parentRect = parent.getBoundingClientRect();
    const imgRect = this.img.getBoundingClientRect();
    this.overlay.style.left = `${imgRect.left - parentRect.left + parent.scrollLeft}px`;
    this.overlay.style.top = `${imgRect.top - parentRect.top + parent.scrollTop}px`;
    this.overlay.style.width = `${imgRect.width}px`;
    this.overlay.style.height = `${imgRect.height}px`;
  }

  private getOverlayParent(): HTMLElement {
    return this.quill.root.parentElement as HTMLElement;
  }
}
